import { classicBoard, parseSquare, SIZE, squareName } from './board.js';
import { DEFAULT_CONFIG, RULES_VERSION, type RulesConfig } from './config.js';
import { RuleError } from './errors.js';
import { agreeDraw, applyAction, resign, timeout } from './game.js';
import { generateBoard, GENERATOR_VERSION } from './generator.js';
import { fromNotation } from './notation.js';
import { newGame } from './setup.js';
import { otherTeam } from './team.js';
import type {
  BoardOrigin,
  GameStart,
  GameState,
  PieceType,
  SetupPiece,
  Team,
  WinReason,
} from './types.js';

export const RECORD_FORMAT = 1;

/** Everything needed to replay a game: the board origin, the start, the rules, the moves. */
export interface GameRecord {
  format: number;
  rulesVersion: string;
  generatorVersion: number;
  board: BoardOrigin;
  /** Custom starting position, or `null` for the standard setup with Ivory to move. */
  start: GameStart | null;
  /** Only the options that differ from the defaults. */
  config: Partial<RulesConfig>;
  moves: string[];
  result: { winner: Team | null; reason: WinReason } | null;
}

/** Optional rules a record may change, with their parsers. */
const CONFIG_KEYS = {
  threefoldRepetition: (v: string) => parseBoolean(v),
  moveLimit: (v: string) => (v === 'null' ? null : parseInteger(v)),
  masonMayBuildTo4: (v: string) => parseBoolean(v),
  fog: (v: string) => parseBoolean(v),
  lookoutMode: (v: string) => (v === 'pin' || v === 'sight' ? v : bad(`Unknown lookoutMode ${v}`)),
  summitHold: (v: string) => (v === '1' ? 1 : v === '2' ? 2 : bad(`Unsupported summitHold ${v}`)),
  summitContested: (v: string) => parseBoolean(v),
  steepSummit: (v: string) => parseBoolean(v),
  lookout: (v: string) => parseBoolean(v),
  beaconMode: (v: string) =>
    v === 'free' || v === 'escorted' ? v : bad(`Unknown beaconMode ${v}`),
  summitMode: (v: string) =>
    v === 'center' || v === 'twin' || v === 'home' ? v : bad(`Unknown summitMode ${v}`),
  startLayout: (v: string) =>
    v === 'standard' || v === 'corners' ? v : bad(`Unknown startLayout ${v}`),
  secondPlayerCompensation: (v: string) =>
    v === 'none' || v === 'opening-build' ? v : bad(`Unknown secondPlayerCompensation ${v}`),
  maxHeight: (v: string) => {
    const n = parseInteger(v);
    return n === 4 || n === 6 ? n : bad(`Unsupported maxHeight ${v}`);
  },
} as const satisfies Partial<Record<keyof RulesConfig, (v: string) => unknown>>;

type ConfigKey = keyof typeof CONFIG_KEYS;

function isConfigKey(key: string): key is ConfigKey {
  return Object.hasOwn(CONFIG_KEYS, key);
}

/** Starting heights of a board. Generated mountains depend on `maxHeight` and `steepSummit`. */
export function heightsForBoard(
  board: BoardOrigin,
  config: Partial<Pick<RulesConfig, 'maxHeight' | 'steepSummit' | 'summitMode'>> = {},
): number[][] {
  switch (board.kind) {
    case 'seed':
      return generateBoard(
        board.seed,
        config.maxHeight ?? 4,
        config.steepSummit ?? false,
        config.summitMode ?? 'center',
      ).heights;
    case 'classic':
      return classicBoard();
    case 'custom':
      return board.heights.map((row) => row.slice());
  }
}

/** A new standard game on a generated mountain ("Mountain #seed"). */
export function newSeededGame(seed: number, config: Partial<RulesConfig> = {}): GameState {
  const heights = heightsForBoard({ kind: 'seed', seed }, config);
  return newGame(heights, undefined, { board: { kind: 'seed', seed }, config });
}

/** A new standard game on the Classic mountain. */
export function newClassicGame(config: Partial<RulesConfig> = {}): GameState {
  return newGame(classicBoard(), undefined, { board: { kind: 'classic' }, config });
}

export function toRecord(state: GameState): GameRecord {
  const config: Partial<RulesConfig> = {};
  for (const key of Object.keys(CONFIG_KEYS) as ConfigKey[]) {
    if (state.config[key] !== DEFAULT_CONFIG[key])
      Object.assign(config, { [key]: state.config[key] });
  }
  return {
    format: RECORD_FORMAT,
    rulesVersion: RULES_VERSION,
    generatorVersion: GENERATOR_VERSION,
    board: state.board,
    start: state.start,
    config,
    moves: state.log.map((entry) => entry.notation),
    result: state.winReason === null ? null : { winner: state.winner, reason: state.winReason },
  };
}

/**
 * Compact text record, for example:
 *
 * ```
 * [Halvard "1"]
 * [Rules "0.1"]
 * [Generator "1"]
 * [Board "seed:1234"]
 * [Result "I summit"]
 *
 * Ce2-e4; Xb9xc7; Lc3 pin c6
 * ```
 */
export function serialize(state: GameState): string {
  const record = toRecord(state);
  const lines = [
    `[Halvard "${record.format}"]`,
    `[Rules "${record.rulesVersion}"]`,
    `[Generator "${record.generatorVersion}"]`,
    `[Board "${formatBoard(record.board)}"]`,
  ];
  if (record.start !== null) {
    lines.push(`[Setup "${record.start.setup.map(formatPiece).join(',')}"]`);
    if (record.start.turn !== 'I') lines.push(`[FirstTurn "${record.start.turn}"]`);
  }
  const config = Object.entries(record.config).map(([k, v]) => `${k}=${String(v)}`);
  if (config.length > 0) lines.push(`[Config "${config.join(',')}"]`);
  if (record.result !== null) {
    lines.push(`[Result "${record.result.winner ?? '-'} ${record.result.reason}"]`);
  }
  return `${lines.join('\n')}\n\n${record.moves.join('; ')}\n`;
}

export function deserialize(text: string): GameRecord {
  const tags = new Map<string, string>();
  const body: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (line === '') continue;
    const tag = /^\[(\w+) "([^"]*)"\]$/.exec(line);
    if (tag) tags.set(tag[1] ?? '', tag[2] ?? '');
    else body.push(line);
  }

  const format = parseInteger(required(tags, 'Halvard'));
  if (format !== RECORD_FORMAT) bad(`Unsupported record format ${format}`);

  const config: Partial<RulesConfig> = {};
  const configTag = tags.get('Config');
  if (configTag !== undefined && configTag !== '') {
    for (const pair of configTag.split(',')) {
      const [key = '', value = ''] = pair.split('=');
      if (!isConfigKey(key)) bad(`Unknown config option "${key}"`);
      Object.assign(config, { [key]: CONFIG_KEYS[key](value) });
    }
  }

  const resultTag = tags.get('Result');
  let result: GameRecord['result'] = null;
  if (resultTag !== undefined) {
    const match =
      /^([IB-]) (capture|summit|stuck|resign|timeout|agreement|repetition|move-limit)$/.exec(
        resultTag,
      );
    if (!match) bad(`Unreadable result "${resultTag}"`);
    const winner = match[1] === '-' ? null : (match[1] as Team);
    result = { winner, reason: match[2] as WinReason };
  }

  const moves = body
    .join(' ')
    .split(';')
    .map((m) => m.trim())
    .filter((m) => m !== '');

  const setupTag = tags.get('Setup');
  const firstTurn = tags.get('FirstTurn') ?? 'I';
  if (firstTurn !== 'I' && firstTurn !== 'B') bad(`Unreadable first turn "${firstTurn}"`);
  const start: GameStart | null =
    setupTag === undefined
      ? firstTurn === 'I'
        ? null
        : bad('[FirstTurn] needs a [Setup] tag')
      : { setup: setupTag === '' ? [] : setupTag.split(',').map(parsePiece), turn: firstTurn };

  return {
    format,
    rulesVersion: required(tags, 'Rules'),
    generatorVersion: parseInteger(required(tags, 'Generator')),
    board: parseBoard(required(tags, 'Board')),
    start,
    config,
    moves,
    result,
  };
}

/**
 * Replays a record and returns the final state, or the state after `plies` actions.
 * Throws a `RuleError` (`BAD_RECORD`, or the code of the first illegal move) if the record
 * does not replay exactly.
 */
export function replay(record: GameRecord, plies = record.moves.length): GameState {
  if (record.rulesVersion !== RULES_VERSION)
    bad(`Unsupported rules version ${record.rulesVersion}`);
  if (record.generatorVersion !== GENERATOR_VERSION) {
    bad(`Unsupported generator version ${record.generatorVersion}`);
  }
  let state = newGame(heightsForBoard(record.board, record.config), record.start?.setup, {
    board: record.board,
    config: record.config,
    ...(record.start ? { turn: record.start.turn } : {}),
  });
  const count = Math.min(Math.max(plies, 0), record.moves.length);
  for (const move of record.moves.slice(0, count)) {
    const { pieceId, action } = fromNotation(state, move);
    state = applyAction(state, pieceId, action);
  }
  if (count < record.moves.length || record.result === null) {
    if (count === record.moves.length && state.winReason !== null)
      bad('Record is missing its result');
    return state;
  }

  const { winner, reason } = record.result;
  if (state.winReason === null) {
    if (reason === 'resign' && winner !== null) return resign(state, otherTeam(winner));
    if (reason === 'timeout' && winner !== null) return timeout(state, otherTeam(winner));
    if (reason === 'agreement' && winner === null) return agreeDraw(state);
  } else if (state.winReason === reason && state.winner === winner) {
    return state;
  }
  return bad(`Result "${winner ?? '-'} ${reason}" does not match the moves`);
}

function formatPiece(piece: SetupPiece): string {
  return `${piece.team}${piece.type}@${squareName(piece.r, piece.c)}`;
}

function parsePiece(text: string): SetupPiece {
  const match = /^([IB])([BCXLM])@([a-i][1-9])$/.exec(text.trim());
  if (!match) return bad(`Unreadable piece "${text}"`);
  const { r, c } = parseSquare(match[3] ?? '');
  return { team: match[1] as Team, type: match[2] as PieceType, r, c };
}

function formatBoard(board: BoardOrigin): string {
  switch (board.kind) {
    case 'seed':
      return `seed:${board.seed}`;
    case 'classic':
      return 'classic';
    case 'custom':
      return `custom:${board.heights.map((row) => row.join('')).join('')}`;
  }
}

function parseBoard(text: string): BoardOrigin {
  if (text === 'classic') return { kind: 'classic' };
  const seed = /^seed:(-?\d+)$/.exec(text);
  if (seed) return { kind: 'seed', seed: parseInteger(seed[1] ?? '') };
  const custom = /^custom:([0-4]{81})$/.exec(text);
  if (custom) {
    const digits = (custom[1] ?? '').split('').map(Number);
    return {
      kind: 'custom',
      heights: Array.from({ length: SIZE }, (_, r) => digits.slice(r * SIZE, (r + 1) * SIZE)),
    };
  }
  return bad(`Unreadable board "${text}"`);
}

function required(tags: Map<string, string>, name: string): string {
  const value = tags.get(name);
  if (value === undefined) bad(`Missing [${name}] tag`);
  return value;
}

function parseInteger(text: string): number {
  if (!/^-?\d+$/.test(text)) bad(`Not an integer: "${text}"`);
  return Number(text);
}

function parseBoolean(text: string): boolean {
  if (text === 'true') return true;
  if (text === 'false') return false;
  return bad(`Not a boolean: "${text}"`);
}

function bad(message: string): never {
  throw new RuleError('BAD_RECORD', message);
}
